import { createSelector, createSlice } from '@reduxjs/toolkit';
import { getNormalizedNavItems } from '../reducers/utils/navItems';
import { EXPANDABLE_NAV_GROUP, EXPANDABLE_NAV_GROUP_MAX_INITIAL_DISPLAY_ITEMS } from '../components/VerticalNav/constants';
import { NAV_ITEM_BOOKMARKS_GROUP_ID } from '../constants/Bookmarks';
export const initialState = {
  byId: {},
  byPath: {},
  primaryNav: {
    navItems: [],
    openTooltip: undefined
  },
  sidebarDefaultsMapping: {},
  openMenus: {
    primary: undefined,
    secondary: []
  },
  activeItem: undefined,
  focusedItem: undefined,
  verticalNavExclusionRoutes: []
};
export const navItemsSlice = createSlice({
  name: 'navItems',
  initialState,
  reducers: {
    hydrate: (state, action) => state = getNormalizedNavItems(action.payload),
    toggleSubmenu: (state, action) => {
      const id = action.payload && action.payload.id;
      state.primaryNav.openTooltip = undefined;
      if (!id || id === state.openMenus.primary) {
        const {
          primary
        } = state.openMenus;
        if (primary) {
          for (const child of state.byId[primary].children) {
            if (child.isExpanded) {
              child.isExpanded = false;
            }
          }
        }
        state.openMenus = {
          primary: undefined,
          secondary: []
        };
        return;
      }
      const {
        ancestors
      } = state.byId[id];
      if (ancestors.length === 0) {
        state.openMenus = {
          primary: id,
          secondary: []
        };
        return;
      }
      if (ancestors.length > 0 && ancestors[0] !== state.openMenus.primary) {
        state.openMenus = {
          primary: ancestors[0],
          secondary: [id]
        };
        return;
      }
      const menuIndex = state.openMenus.secondary.indexOf(id);
      if (menuIndex === -1) {
        state.openMenus.secondary.push(id);
        return;
      }
      state.openMenus.secondary.splice(menuIndex, 1);
    },
    setFocusOnNavItem: (state, action) => {
      const {
        id
      } = action.payload;
      state.focusedItem = id;
    },
    resetFocus: state => {
      state.focusedItem = undefined;
    },
    setActiveNavItem: (state, action) => {
      const {
        id
      } = action.payload;
      state.activeItem = id;
    },
    setExpandableMenuItemVisibility: (state, action) => {
      const {
        menuId,
        isExpanded
      } = action.payload;
      const openPrimaryMenuItem = state.openMenus.primary;
      if (!openPrimaryMenuItem) return;
      const expandableMenuIndex = state.byId[openPrimaryMenuItem].children.findIndex(({
        id
      }) => id === menuId);
      if (expandableMenuIndex >= 0) {
        const expandableMenuItem = state.byId[openPrimaryMenuItem].children[expandableMenuIndex];
        expandableMenuItem.isExpanded = isExpanded;
        if (isExpanded) {
          state.focusedItem = expandableMenuItem.children[EXPANDABLE_NAV_GROUP_MAX_INITIAL_DISPLAY_ITEMS].id;
        }
      }
    },
    updateNavItemBookmarkState: (state, action) => {
      const {
        isBookmarked,
        id
      } = action.payload;
      if (state.byId[id]) {
        state.byId[id].isBookmarked = isBookmarked;
      }
      if (state.byId[NAV_ITEM_BOOKMARKS_GROUP_ID]) {
        if (isBookmarked) {
          state.byId[NAV_ITEM_BOOKMARKS_GROUP_ID].children.push({
            id,
            children: []
          });
        } else {
          state.byId[NAV_ITEM_BOOKMARKS_GROUP_ID].children = state.byId[NAV_ITEM_BOOKMARKS_GROUP_ID].children.filter(bookmark => bookmark.id !== id);
        }
      }
    },
    updateNavItemTrialState: (state, action) => {
      const {
        inTrial
      } = action.payload;
      inTrial.forEach(id => {
        if (state.byId[id]) {
          state.byId[id].fromTrial = true;
        }
      });
    },
    togglePrimaryNavTooltip: (state, action) => {
      const {
        id
      } = action.payload;
      if (state.openMenus.primary) {
        state.openMenus.primary = undefined;
        state.openMenus.secondary = [];
      }
      if (state.primaryNav.openTooltip === id || !id) {
        state.primaryNav.openTooltip = undefined;
        return;
      }
      state.primaryNav.openTooltip = id;
    }
  }
});
const getNavItemsById = state => state.navItems.byId;
const getOpenPrimaryMenu = state => state.navItems.openMenus.primary;
const getOpenPrimaryMenuNavItem = createSelector([getNavItemsById, getOpenPrimaryMenu], (navItemsById, openPrimaryMenu) => {
  if (!openPrimaryMenu) return undefined;
  return navItemsById[openPrimaryMenu];
});
export const getIsExpanded = createSelector([getOpenPrimaryMenuNavItem, (_, menuId) => menuId], (openMenuItem, menuId) => {
  if (!openMenuItem || !openMenuItem.children || !menuId.startsWith(EXPANDABLE_NAV_GROUP)) {
    return false;
  }
  const expandableMenu = openMenuItem.children.find(item => item.id === menuId);
  if (!expandableMenu) return false;
  return !!expandableMenu.isExpanded;
});
export const {
  hydrate,
  toggleSubmenu,
  setFocusOnNavItem,
  setActiveNavItem,
  setExpandableMenuItemVisibility,
  resetFocus,
  updateNavItemBookmarkState,
  updateNavItemTrialState,
  togglePrimaryNavTooltip
} = navItemsSlice.actions;
export default navItemsSlice.reducer;